import React from "react";
import { Download, Loader2 } from "lucide-react";
import {
  useSmilesExport,
  SmilesExportMode,
  SmilesExportFilters,
} from "../../hooks/useSmilesExport";

interface SmilesExportButtonProps {
  mode: SmilesExportMode;
  filters?: SmilesExportFilters;
  scopeSlug?: string;
  disabled?: boolean;
  label?: string;
}

const SmilesExportButton: React.FC<SmilesExportButtonProps> = ({
  mode,
  filters,
  scopeSlug,
  disabled = false,
  label = "Download SMILES",
}) => {
  const { start, cancel, status, loaded, total, error } = useSmilesExport({
    mode,
    ...(filters !== undefined ? { filters } : {}),
    ...(scopeSlug !== undefined ? { scopeSlug } : {}),
  });

  const isLoading = status === "loading" || status === "cancelling";

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={start}
          disabled={disabled || isLoading}
          aria-label={label}
          className="flex items-center gap-1.5 h-9 px-3 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              <span>
                {loaded.toLocaleString()} of {total.toLocaleString()}
              </span>
            </>
          ) : (
            <>
              <Download size={14} />
              <span>{label}</span>
            </>
          )}
        </button>

        {isLoading && (
          <button
            type="button"
            onClick={cancel}
            disabled={status === "cancelling"}
            className="flex items-center h-9 px-3 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 border border-gray-200 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {status === "cancelling" ? "Cancelling…" : "Cancel"}
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1 max-w-xs">
          {error}
        </div>
      )}
    </div>
  );
};

export default SmilesExportButton;
